import { useAuthentication } from '@ecosystem/newron-client-lib';
import { MutateOptions, useMutation, UseMutationOptions } from '@tanstack/react-query';

import { FilterPersistenceAsyncService } from './FilterPersistenceService';
import { PreferenceAPIFilterPersistenceService } from './PreferenceApiFilterPersistenceService';
import { FilterPersistenceResponse, SaveFilterRequest } from './Types';

import { useUserPersistedFilters } from './UseUserPersistedFilters';

export type SavePersistedFilterMutateOptions = MutateOptions<FilterPersistenceResponse, unknown, SaveFilterRequest>;

export interface SavePersistedFilter {
  mutate: (variables: SaveFilterRequest, options?: SavePersistedFilterMutateOptions) => void;
  data: FilterPersistenceResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isSuccess: boolean;
  reset: () => void;
}

export const useSavePersistedFilter = (
  request?: Partial<SaveFilterRequest>,
  options?: Omit<
    UseMutationOptions<FilterPersistenceResponse, unknown, SaveFilterRequest, unknown>,
    'mutationKey' | 'mutationFn'
  >,
  service: FilterPersistenceAsyncService = PreferenceAPIFilterPersistenceService,
): SavePersistedFilter => {
  const { getAccessToken } = useAuthentication();
  const { refetch } = useUserPersistedFilters();

  const onSuccess: (successData: FilterPersistenceResponse) => void = (successData: FilterPersistenceResponse) => {
    if (successData.status === 'SUCCESS') {
      refetch();
    }
  };

  const { data, isPending, isError, isSuccess, error, mutate, reset } = useMutation({
    mutationKey: ['Save filter', request?.context, request?.values],

    mutationFn: async (mutationRequest: SaveFilterRequest) => {
      let userToken = await getAccessToken();
      if (!userToken) {
        throw new Error('Failed to get userToken');
      }
      return service.saveFilter(userToken, request?.context!, mutationRequest);
    },

    ...options,
    onSuccess,
  });

  return {
    data: data ?? null,
    isLoading: isPending,
    isError,
    isSuccess,
    error,
    mutate,
    reset,
  };
};
